import type { CoverageLevel, CompanyCatalogItem, CatalogItemType, BenefitElection, CreateBenefitElectionInput } from '../../benefits-deductions/types';
import type { CreateDeductionElectionInput, DeductionElectionResponse } from '../../compensation/types';

/** The pure logic behind Employee Profile Setup > Benefits / Deductions: what a row shows, which fields an assignment offers for a catalog item, how they are checked, and the
 * body each write sends. Nothing here knows a particular benefit or deduction; everything comes from the catalog item the server returned. */

export const todayIso = () => new Date().toISOString().slice(0, 10);

export function dayAfter(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** An assignment is listed while it has no end date or its end date is still ahead. Closed-out history stays in the database and is never shown as a current row. */
export function isCurrent(end: string | null, today: string): boolean {
  return end === null || end > today;
}

/** Plain decimal, at most two places, never negative: what the stored amount columns can hold. */
export function parseAmount(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Number(t);
}

export const formatMoney = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
export const formatPercent = (value: number) => `${value}%`;

export const isPercentMethod = (item: CompanyCatalogItem | undefined) => item?.calculationMethod?.includes('Percent') ?? false;

/** A plain-language name for a calculation method the server sent (a contribution method for a plan, a calculation type for a deduction). */
const METHOD_LABELS: Record<string, string> = {
  FixedAmount: 'Fixed amount',
  PercentOfEligibleGross: 'Percent of eligible wages',
  PercentOfDisposableEarnings: 'Percent of disposable earnings',
  ApprovedRetirementElectionPercent: 'Percent (retirement election)',
  ApprovedGarnishmentFormula: 'Approved garnishment formula',
  RemainingBalanceUpToPerPayrollAmount: 'Up to a per-payroll amount until paid',
  FixedPerPayroll: 'Fixed amount per payroll',
  FixedPerMonth: 'Fixed amount per month',
  PercentOfEligiblePay: 'Percent of eligible pay',
  EmployerPaidPercent: 'Employer-paid percent',
  EmployeePaidPercent: 'Employee-paid percent',
  TieredByCoverage: 'By coverage level',
  NoPayrollContribution: 'No payroll contribution',
};
export const methodLabel = (method: string | null) => (method === null ? '—' : (METHOD_LABELS[method] ?? method));

const FREQUENCY_LABELS: Record<string, string> = {
  EveryPayroll: 'Every payroll', Monthly: 'Monthly', Quarterly: 'Quarterly', Annually: 'Annually', OneTime: 'One time', PerPayroll: 'Every payroll',
};
export const frequencyLabel = (frequency: string | null) => (frequency === null ? '—' : (FREQUENCY_LABELS[frequency] ?? frequency));

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------------------------------------------------------

export interface AssignmentRow {
  key: string;
  kind: CatalogItemType;
  electionId: string;
  itemId: string;
  /** Undefined when the catalog could not name it (for example the half of the catalog that failed to load). */
  item: CompanyCatalogItem | undefined;
  title: string | null;
  employee: { amount: number | null; percent: number | null };
  employer: { amount: number | null; percent: number | null };
  coverageLevel: CoverageLevel | null;
  isWaived: boolean;
  /** A deduction's employee-specific annual limit (recorded only; payroll does not enforce limits yet). */
  annualLimit: number | null;
  /** The server's version of the election: sent when ending it, so a stale screen cannot overwrite a newer change. */
  concurrencyVersion: number;
  start: string;
  end: string | null;
}

export function deductionRow(e: DeductionElectionResponse, item: CompanyCatalogItem | undefined): AssignmentRow {
  const percent = isPercentMethod(item);
  const employee = e.amountOverride ?? (percent ? item?.defaultEmployeePercent : item?.defaultEmployeeAmount) ?? null;
  const employer = e.companyContributionOverride ?? (percent ? item?.defaultEmployerPercent : item?.defaultEmployerAmount) ?? null;
  return {
    key: `Deduction:${e.id}`, kind: 'Deduction', electionId: e.id, itemId: e.deductionCodeId, item, title: e.title, coverageLevel: null, isWaived: false,
    annualLimit: e.employeeAnnualLimit ?? null, concurrencyVersion: e.concurrencyVersion ?? 1,
    employee: percent ? { amount: null, percent: employee } : { amount: employee, percent: null },
    employer: percent ? { amount: null, percent: employer } : { amount: employer, percent: null },
    start: e.effectiveStartDate, end: e.effectiveEndDate,
  };
}

export function benefitRow(e: BenefitElection, item: CompanyCatalogItem | undefined): AssignmentRow {
  return {
    key: `Benefit:${e.id}`, kind: 'Benefit', electionId: e.id, itemId: e.benefitCodeId, item, title: null, coverageLevel: e.coverageLevel, isWaived: e.isWaived,
    annualLimit: null, concurrencyVersion: e.concurrencyVersion,
    employee: { amount: e.amountOverride ?? item?.defaultEmployeeAmount ?? null, percent: e.employeeContributionPercentOverride ?? item?.defaultEmployeePercent ?? null },
    employer: { amount: item?.defaultEmployerAmount ?? null, percent: e.companyContributionPercentOverride ?? item?.defaultEmployerPercent ?? null },
    start: e.effectiveStartDate, end: e.effectiveEndDate,
  };
}

export function contributionText(side: { amount: number | null; percent: number | null }): string {
  if (side.percent !== null) return formatPercent(side.percent);
  if (side.amount !== null) return formatMoney(side.amount);
  return '—';
}

export type RowBadge = 'Inactive' | 'Requires payroll setup';

/** Why a row's catalog item is not live: an inactive item is "Inactive"; one that is awaiting tax review or a supported calculation "Requires payroll setup". */
export function rowBadge(item: CompanyCatalogItem | undefined): RowBadge | null {
  if (!item) return null;
  if (!item.isActive) return 'Inactive';
  if (item.notAssignableCode === 'CONFIGURATION_INCOMPLETE' || item.notAssignableCode === 'CALCULATION_NOT_SUPPORTED_YET') return 'Requires payroll setup';
  return null;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Fields an assignment offers for a catalog item
// ---------------------------------------------------------------------------------------------------------------------------------------------

/** `amount` and `percent` are editable (the unit differs), `readonly` shows the definition's own value, `none` means the item carries nothing on that side. */
export type FieldMode = 'amount' | 'percent' | 'readonly' | 'none';

export interface FieldConfig {
  employee: FieldMode;
  employer: FieldMode;
  needsCoverage: boolean;
  noPayrollContribution: boolean;
}

export function fieldConfig(item: CompanyCatalogItem): FieldConfig {
  const method = item.calculationMethod;
  if (item.itemType === 'Deduction') {
    const mode: FieldMode = isPercentMethod(item) ? 'percent' : 'amount';
    return { employee: mode, employer: mode, needsCoverage: false, noPayrollContribution: false };
  }
  if (method === null || method === 'NoPayrollContribution') return { employee: 'none', employer: 'none', needsCoverage: false, noPayrollContribution: true };
  if (method === 'TieredByCoverage') return { employee: 'readonly', employer: 'readonly', needsCoverage: true, noPayrollContribution: false };
  if (method.includes('Percent')) {
    return {
      employee: item.defaultEmployeePercent !== null ? 'percent' : 'none',
      employer: item.defaultEmployerPercent !== null ? 'percent' : 'none',
      needsCoverage: false, noPayrollContribution: false,
    };
  }
  return {
    employee: item.defaultEmployeeAmount !== null ? 'amount' : 'none',
    employer: item.defaultEmployerAmount !== null ? 'readonly' : 'none',
    needsCoverage: false, noPayrollContribution: false,
  };
}

export interface AssignmentForm {
  employee: string;
  employer: string;
  coverageLevel: CoverageLevel | '';
  title: string;
  effectiveDate: string;
  /** Optional: when the assignment stops applying (after the effective date). */
  endDate: string;
  /** Optional, a deduction only: this employee's own annual limit. */
  annualLimit: string;
}

export const emptyAssignmentForm = (): AssignmentForm => ({ employee: '', employer: '', coverageLevel: '', title: '', effectiveDate: todayIso(), endDate: '', annualLimit: '' });

export type AssignmentErrors = Partial<Record<keyof AssignmentForm | 'item', string>>;

export interface Validation {
  errors: AssignmentErrors;
  /** Errors that are only an empty required field: not shown while the person is still filling the form in. */
  missing: Set<keyof AssignmentErrors>;
}

const NUMBER_HELP = 'Enter a number of 0 or more with at most two decimal places.';

function checkValue(raw: string, mode: FieldMode, label: string, required: boolean, key: 'employee' | 'employer', next: AssignmentErrors, missing: Set<keyof AssignmentErrors>) {
  if (mode !== 'amount' && mode !== 'percent') return;
  if (raw.trim() === '') {
    if (required) { next[key] = `Enter the ${label}.`; missing.add(key); }
    return;
  }
  const value = parseAmount(raw);
  if (value === null) next[key] = NUMBER_HELP;
  else if (required && value === 0) next[key] = `The ${label} must be greater than 0.`;
  else if (mode === 'percent' && value > 100) next[key] = 'A percentage cannot be more than 100.';
}

export function validateAssignment(
  item: CompanyCatalogItem | null, form: AssignmentForm, opts: { today: string; isEdit: boolean; alreadyAssigned: boolean },
): Validation {
  const errors: AssignmentErrors = {};
  const missing = new Set<keyof AssignmentErrors>();
  if (!item) {
    errors.item = 'Choose an item from the company catalog.';
    missing.add('item');
    return { errors, missing };
  }
  const config = fieldConfig(item);
  if (!item.assignable) errors.item = item.notAssignableReason ?? 'This item cannot be assigned yet.';
  else if (opts.alreadyAssigned && !opts.isEdit) errors.item = 'This is already assigned to this employee. Use Edit on its row to change it from a new date.';

  // A deduction always carries an employee contribution; a benefit's is optional (the plan's own value applies when it is left empty).
  checkValue(form.employee, config.employee, 'employee contribution', item.itemType === 'Deduction', 'employee', errors, missing);
  checkValue(form.employer, config.employer, 'employer contribution', false, 'employer', errors, missing);

  if (config.needsCoverage && !form.coverageLevel) { errors.coverageLevel = 'Choose a coverage level this plan offers.'; missing.add('coverageLevel'); }
  if (form.title.trim().length > 100) errors.title = 'The title must be 100 characters or fewer.';
  if (item.itemType === 'Deduction' && form.annualLimit.trim() !== '' && parseAmount(form.annualLimit) === null) errors.annualLimit = NUMBER_HELP;
  if (form.endDate !== '' && form.effectiveDate !== '' && form.endDate <= form.effectiveDate) errors.endDate = 'The end date must be after the effective date.';
  if (!form.effectiveDate) { errors.effectiveDate = 'Choose the date this takes effect.'; missing.add('effectiveDate'); }
  else if (opts.isEdit && form.effectiveDate < opts.today) errors.effectiveDate = 'A change cannot take effect before today. Earlier payroll is never rewritten.';
  return { errors, missing };
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Write bodies
// ---------------------------------------------------------------------------------------------------------------------------------------------

const collapse = (text: string) => text.replace(/\s+/g, ' ').trim();

/** `replaceCurrent` is true only when editing: it tells the server this change replaces the employee's current assignment from the new date (otherwise a second one is refused). */
export function deductionBody(item: CompanyCatalogItem, form: AssignmentForm, replaceCurrent = false): CreateDeductionElectionInput {
  const config = fieldConfig(item);
  return {
    deductionCodeId: item.id,
    amountOverride: config.employee === 'none' ? null : parseAmount(form.employee),
    companyContributionOverride: form.employer.trim() === '' || config.employer === 'none' ? null : parseAmount(form.employer),
    title: collapse(form.title) === '' ? null : collapse(form.title),
    effectiveStartDate: form.effectiveDate,
    effectiveEndDate: form.endDate === '' ? null : form.endDate,
    employeeAnnualLimit: form.annualLimit.trim() === '' ? null : parseAmount(form.annualLimit),
    ...(replaceCurrent ? { replaceCurrent: true } : {}),
  };
}

export function benefitBody(item: CompanyCatalogItem, form: AssignmentForm, replaceCurrent = false): CreateBenefitElectionInput {
  const config = fieldConfig(item);
  const employee = form.employee.trim() === '' ? null : parseAmount(form.employee);
  const employer = form.employer.trim() === '' ? null : parseAmount(form.employer);
  return {
    benefitCodeId: item.id,
    amountOverride: config.employee === 'amount' ? employee : null,
    employeeContributionPercentOverride: config.employee === 'percent' ? employee : null,
    companyContributionPercentOverride: config.employer === 'percent' ? employer : null,
    effectiveStartDate: form.effectiveDate,
    coverageLevel: config.needsCoverage && form.coverageLevel !== '' ? form.coverageLevel : null,
    isWaived: false,
    effectiveEndDate: form.endDate === '' ? null : form.endDate,
    ...(replaceCurrent ? { replaceCurrent: true } : {}),
  };
}

/** Starting values for the form when a row is opened for editing: the row's own overrides, else empty so the definition's default shows through. */
export function formFromRow(row: AssignmentRow, today: string): AssignmentForm {
  const mode = row.item ? fieldConfig(row.item) : null;
  const pick = (side: { amount: number | null; percent: number | null }, m: FieldMode | undefined) =>
    m === 'percent' ? side.percent : m === 'amount' ? side.amount : null;
  const employee = pick(row.employee, mode?.employee);
  const employer = pick(row.employer, mode?.employer);
  return {
    employee: employee === null ? '' : String(employee),
    employer: employer === null ? '' : String(employer),
    coverageLevel: row.coverageLevel ?? '',
    title: row.title ?? '',
    effectiveDate: row.start > today ? row.start : today,
    endDate: row.end ?? '',
    annualLimit: row.annualLimit === null ? '' : String(row.annualLimit),
  };
}
